import { Link, useLocation } from "react-router-dom";
import { useApp } from "@/contexts/AppContext";
import { useEffect, useState, useRef } from "react";
import { supabase } from "@/lib/supabase";
import { Menu, X, Tractor, ChevronDown, LayoutDashboard, MessageCircle, ShieldCheck, LogOut, User, Heart, BadgeCheck, Mail, TrendingUp} from "lucide-react";
import { Button } from "@/components/ui/button";
import UserAvatar from "@/components/UserAvatar";
import NotificationBell from "@/components/NotificationBell";

export default function Navbar() {
  const { user, logout } = useApp();
  const location = useLocation();
  const showAnuncieBtn = location.pathname !== "/criar-anuncio";
  const [open, setOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [plan, setPlan] = useState<any>(null);
  const [verif, setVerif] = useState<any>(null);
  const [isAffiliate, setIsAffiliate] = useState(false);
  function loadPlanData() {
    if (!user) { setPlan(null); return; }
    supabase.rpc("my_plan_summary").then(({ data }) => setPlan(data || null));
    supabase.rpc("my_verification_status").then(({ data }) => setVerif(data || null));
    supabase.rpc("my_affiliate_summary").then(({ data }) => setIsAffiliate((data as any)?.is_affiliate || false));
  }
  useEffect(() => {
    loadPlanData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);
  useEffect(() => {
    const h = () => loadPlanData();
    window.addEventListener("plan-updated", h);
    return () => window.removeEventListener("plan-updated", h);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [pendingCount, setPendingCount] = useState(0);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!user) { setIsAdmin(false); return; }
    supabase.rpc("get_my_profile").select("is_admin").single()
      .then(({ data }) => {
        setIsAdmin(data?.is_admin || false);
        if (data?.is_admin) {
          supabase.from("listings").select("id", { count: "exact", head: true }).eq("status", "pending")
            .then(({ count }) => setPendingCount(count || 0));
        }
      });
  }, [user]);

  useEffect(() => {
    if (!user) { setUnreadCount(0); return; }
    fetchUnread();
    const channel = supabase
      .channel("unread-messages")
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "messages" }, () => fetchUnread())
      .on("postgres_changes", { event: "UPDATE", schema: "public", table: "messages" }, () => fetchUnread())
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [user]);

  async function fetchUnread() {
    // versao simplificada
    // versao simplificada
    if (!user) return;
    const { data: convs } = await supabase
      .from("conversations")
      .select("id")
      .or(`buyer_id.eq.${user.id},seller_id.eq.${user.id}`);
    if (!convs || convs.length === 0) { setUnreadCount(0); return; }
    const convIds = convs.map((c: any) => c.id);
    const { count } = await supabase
      .from("messages")
      .select("*", { count: "exact", head: true })
      .in("conversation_id", convIds)
      .or("read.eq.false,read.is.null")
      .neq("sender_id", user.id);
    setUnreadCount(count || 0);
  }

  useEffect(() => {
    if (location.pathname === "/mensagens") {
      setTimeout(fetchUnread, 1000);
    }
  }, [location.pathname]);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <nav className="bg-primary text-primary-foreground sticky top-0 z-50 shadow-lg">
      <div className="container mx-auto flex items-center justify-between h-16 px-4">
        <Link to="/" className="flex items-center gap-2 font-heading text-xl font-bold">
          <Tractor className="h-7 w-7 text-accent" />
          AgroAluga
        </Link>

        <div className="hidden md:flex items-center gap-6 text-sm font-medium">
          <Link to="/como-funciona" className="hover:text-accent transition-colors">Como Funciona</Link>
          
          <Link to="/beneficios" className="hover:text-accent transition-colors">Benefícios</Link>
          <Link to="/planos" className="hover:text-accent transition-colors font-semibold text-accent">Planos</Link>
        </div>

        <div className="hidden md:flex items-center gap-3">
          {showAnuncieBtn && (
            <Link to={user ? "/criar-anuncio" : "/cadastro"}>
              <Button className="bg-accent text-accent-foreground hover:bg-accent/90 font-semibold gap-2 shadow-sm">
                + Anuncie agora
              </Button>
            </Link>
          )}
          {user && <NotificationBell />}
          {user ? (
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className="flex items-center gap-2 hover:opacity-80 transition-opacity px-2 py-1 rounded-lg hover:bg-primary-medium"
              >
                <div className="relative">
                  <UserAvatar userId={user.id} name={user.name} size="sm" />
                  {unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 h-4 w-4 bg-destructive text-white text-xs rounded-full flex items-center justify-center font-bold">
                      {unreadCount > 9 ? "9+" : unreadCount}
                    </span>
                  )}
                </div>
                <span className="text-sm font-medium">{user.name.split(" ")[0]}</span>
                <ChevronDown className={`h-4 w-4 transition-transform ${dropdownOpen ? "rotate-180" : ""}`} />
              </button>

              {dropdownOpen && (
                <div className="absolute right-0 top-12 w-52 bg-card border border-border rounded-lg shadow-xl py-1 z-50">
                  <div className="px-4 py-2 border-b border-border">
                    <p className="font-medium text-sm text-card-foreground">{user.name}</p>
                    <p className="text-xs text-muted-foreground truncate">{user.email}</p>
                    {plan && (
                      <div className="mt-2">
                        <span className={`inline-block text-[10px] font-bold px-2 py-0.5 rounded-full ${
                          plan.is_admin ? "bg-primary text-primary-foreground" :
                          plan.plan_id === "ultra" ? "bg-yellow-400 text-yellow-900" :
                          plan.plan_id === "pro" ? "bg-primary/15 text-primary" :
                          plan.plan_id === "plus" ? "bg-blue-100 text-blue-700" :
                          "bg-muted text-muted-foreground"}`}>
                          {plan.plan_name}
                        </span>
                        {!plan.is_admin && (
                          <div className="mt-1.5 space-y-0.5 text-[11px] text-muted-foreground">
                            <div>Anúncios: <span className="font-semibold text-foreground">{plan.listings_left === -1 ? "Ilimitado" : `${plan.listings_left} restantes`}</span></div>
                            <div>Destaques: <span className="font-semibold text-foreground">{plan.featured_left === -1 ? "Ilimitado" : `${plan.featured_left} restantes`}</span></div>
                          </div>
                        )}
                        {!plan.is_admin && (
                          <Link to="/planos" onClick={() => setDropdownOpen(false)} className="block mt-1.5 text-[11px] font-medium text-primary hover:underline">
                            Ver planos →
                          </Link>
                        )}
                        {verif && (
                          <div className="mt-2 pt-2 border-t border-border space-y-1">
                            {verif.email_confirmed ? (
                              <div className="flex items-center gap-1 text-[11px] text-green-700">
                                <BadgeCheck className="h-3 w-3" /> E-mail confirmado
                              </div>
                            ) : (
                              <div className="flex items-center gap-1 text-[11px] text-amber-700">
                                <Mail className="h-3 w-3" /> Confirme seu e-mail
                              </div>
                            )}
                            {verif.is_verified_advertiser ? (
                              <div className="flex items-center gap-1 text-[11px] font-semibold text-blue-700">
                                <BadgeCheck className="h-3 w-3" /> Anunciante Verificado
                              </div>
                            ) : !plan.is_admin && (
                              <Link to="/planos" onClick={() => setDropdownOpen(false)} className="flex items-center gap-1 text-[11px] font-medium text-blue-700 hover:underline">
                                <BadgeCheck className="h-3 w-3" /> Seja Verificado → assine Pro
                              </Link>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  <Link to="/perfil" onClick={() => setDropdownOpen(false)}
                    className="flex items-center gap-3 px-4 py-2.5 text-sm text-card-foreground hover:bg-secondary transition-colors">
                    <User className="h-4 w-4 text-muted-foreground" /> Meu Perfil
                  </Link>
                  {isAffiliate && (
                    <Link to="/afiliado" onClick={() => setDropdownOpen(false)}
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-card-foreground hover:bg-secondary transition-colors">
                      <TrendingUp className="h-4 w-4 text-muted-foreground" /> Área do Afiliado
                    </Link>
                  )}
                  <Link to="/favoritos" onClick={() => setDropdownOpen(false)}
                    className="flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-secondary transition-colors text-foreground">
                    <Heart className="h-4 w-4 text-muted-foreground" /> Favoritos
                  </Link>
                  <Link to="/dashboard" onClick={() => setDropdownOpen(false)}
                    className="flex items-center gap-3 px-4 py-2.5 text-sm text-card-foreground hover:bg-secondary transition-colors">
                    <LayoutDashboard className="h-4 w-4 text-muted-foreground" /> Meus Anúncios
                  </Link>
                  <Link to="/mensagens" onClick={() => { setDropdownOpen(false); }}
                    className="flex items-center gap-3 px-4 py-2.5 text-sm text-card-foreground hover:bg-secondary transition-colors">
                    <div className="relative">
                      <MessageCircle className="h-4 w-4 text-muted-foreground" />
                      {unreadCount > 0 && (
                        <span className="absolute -top-1 -right-1 h-3 w-3 bg-destructive rounded-full" />
                      )}
                    </div>
                    Mensagens
                    {unreadCount > 0 && (
                      <span className="ml-auto bg-destructive text-white text-xs px-1.5 py-0.5 rounded-full font-bold">
                        {unreadCount > 9 ? "9+" : unreadCount}
                      </span>
                    )}
                  </Link>
                  {isAdmin && (
                    <Link to="/admin" onClick={() => setDropdownOpen(false)}
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-card-foreground hover:bg-secondary transition-colors">
                      <ShieldCheck className="h-4 w-4 text-muted-foreground" /> Admin
                      {pendingCount > 0 && (
                        <span className="ml-1 bg-destructive text-white text-xs font-bold px-1.5 py-0.5 rounded-full leading-none">{pendingCount}</span>
                      )}
                    </Link>
                  )}
                  <div className="border-t border-border mt-1">
                    <button
                      onClick={() => { logout(); setDropdownOpen(false); }}
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-destructive hover:bg-secondary transition-colors w-full text-left"
                    >
                      <LogOut className="h-4 w-4" /> Sair
                    </button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <>
              <Link to="/login">
                <Button variant="ghost" className="text-primary-foreground hover:text-accent hover:bg-primary-medium">Entrar</Button>
              </Link>
              <Link to="/cadastro">
                <Button className="bg-accent text-accent-foreground hover:bg-accent/90 font-semibold">Cadastrar</Button>
              </Link>
            </>
          )}
        </div>

        <div className="md:hidden flex items-center gap-2">
          {user && <NotificationBell />}
          {user ? (
            <button
              onClick={() => setOpen(!open)}
              className="flex items-center gap-2 bg-primary-medium hover:bg-primary-medium/80 transition-colors rounded-full pl-1 pr-3 py-1"
            >
              <div className="relative">
                <UserAvatar userId={user.id} name={user.name} size="sm" />
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 h-4 w-4 bg-destructive text-white text-xs rounded-full flex items-center justify-center font-bold">
                    {unreadCount > 9 ? "9+" : unreadCount}
                  </span>
                )}
              </div>
              <span className="text-sm font-semibold text-primary-foreground max-w-[90px] truncate">
                {user.name.split(" ")[0]}
              </span>
              <ChevronDown className={`h-3.5 w-3.5 text-primary-foreground transition-transform ${open ? "rotate-180" : ""}`} />
            </button>
          ) : (
            <div className="flex items-center gap-2">
              <Link to="/login">
                <Button size="sm" variant="ghost" className="text-primary-foreground hover:text-accent hover:bg-primary-medium text-sm px-3">
                  Entrar
                </Button>
              </Link>
              <button onClick={() => setOpen(!open)} className="text-primary-foreground">
                <Menu className="h-5 w-5" />
              </button>
            </div>
          )}
        </div>
      </div>

      {open && (
        <div className="md:hidden bg-primary border-t border-primary-medium px-4 pb-4">
          <div className="space-y-1 pt-2">
            <Link to="/como-funciona" className="block py-2.5 hover:text-accent transition-colors text-sm" onClick={() => setOpen(false)}>Como Funciona</Link>
            
            <Link to="/beneficios" className="block py-2.5 hover:text-accent transition-colors text-sm" onClick={() => setOpen(false)}>Benefícios</Link>
            <Link to="/planos" className="block py-2.5 hover:text-accent transition-colors text-sm font-semibold text-accent" onClick={() => setOpen(false)}>Planos</Link>
            {showAnuncieBtn && (
              <Link to={user ? "/criar-anuncio" : "/cadastro"}
                className="block py-2.5 text-accent font-semibold hover:text-accent/80 text-sm"
                onClick={() => setOpen(false)}>
                + Anuncie agora
              </Link>
            )}
          </div>

          {user ? (
            <>
              <div className="mt-4 rounded-2xl bg-black/15 border border-white/10 shadow-lg overflow-hidden">
                <div className="flex items-center gap-3 px-4 py-3 bg-white/5 border-b border-white/10">
                  <UserAvatar userId={user.id} name={user.name} size="sm" />
                  <span className="text-sm font-semibold">{user.name}</span>
                </div>
                {plan && (
                <div className="px-4 py-3">
                  <span className={`inline-block text-[10px] font-bold px-2.5 py-1 rounded-full shadow-sm ${
                    plan.is_admin ? "bg-accent text-accent-foreground" :
                    plan.plan_id === "ultra" ? "bg-yellow-400 text-yellow-900" :
                    plan.plan_id === "pro" ? "bg-blue-400 text-white" :
                    plan.plan_id === "plus" ? "bg-blue-200 text-blue-900" :
                    "bg-white/20 text-white"}`}>
                    {plan.plan_name}
                  </span>
                  {!plan.is_admin && (
                    <div className="mt-2 grid grid-cols-2 gap-2">
                      <div className="rounded-lg bg-black/20 px-2.5 py-1.5">
                        <p className="text-[9px] uppercase tracking-wide text-white/50">Anúncios</p>
                        <p className="text-xs font-bold text-white">{plan.listings_left === -1 ? "Ilimitado" : `${plan.listings_left} rest.`}</p>
                      </div>
                      <div className="rounded-lg bg-black/20 px-2.5 py-1.5">
                        <p className="text-[9px] uppercase tracking-wide text-white/50">Destaques</p>
                        <p className="text-xs font-bold text-white">{plan.featured_left === -1 ? "Ilimitado" : `${plan.featured_left} rest.`}</p>
                      </div>
                    </div>
                  )}
                  {verif && (
                    <div className="mt-2 flex flex-wrap gap-2 text-[11px]">
                      {verif.email_confirmed && <span className="inline-flex items-center gap-1 bg-green-500/20 text-green-300 px-2 py-0.5 rounded-full">✓ E-mail</span>}
                      {verif.is_verified_advertiser && <span className="inline-flex items-center gap-1 bg-blue-500/20 text-blue-200 font-semibold px-2 py-0.5 rounded-full">✓ Verificado</span>}
                    </div>
                  )}
                  {!plan.is_admin && (
                    <Link to="/planos" onClick={() => setOpen(false)} className="block mt-2 text-[11px] font-semibold text-accent hover:underline">
                      Ver planos →
                    </Link>
                  )}
                </div>
                )}
              </div>
              <div className="mt-3 rounded-2xl bg-black/10 border border-white/10 shadow-md overflow-hidden divide-y divide-white/5">
                <Link to="/perfil" className="flex items-center gap-3 px-4 py-3 hover:bg-white/5 text-sm transition-colors" onClick={() => setOpen(false)}><User className="h-4 w-4 text-white/60" /> Meu Perfil</Link>
                {isAffiliate && <Link to="/afiliado" className="flex items-center gap-3 px-4 py-3 hover:bg-white/5 text-sm transition-colors" onClick={() => setOpen(false)}><TrendingUp className="h-4 w-4 text-white/60" /> Área do Afiliado</Link>}
                <Link to="/favoritos" className="flex items-center gap-3 px-4 py-3 hover:bg-white/5 text-sm transition-colors" onClick={() => setOpen(false)}><Heart className="h-4 w-4 text-white/60" /> Favoritos</Link>
                <Link to="/dashboard" className="flex items-center gap-3 px-4 py-3 hover:bg-white/5 text-sm transition-colors" onClick={() => setOpen(false)}><LayoutDashboard className="h-4 w-4 text-white/60" /> Meus Anúncios</Link>
                <Link to="/mensagens" className="flex items-center justify-between px-4 py-3 hover:bg-white/5 text-sm transition-colors" onClick={() => setOpen(false)}>
                  <span className="flex items-center gap-3"><MessageCircle className="h-4 w-4 text-white/60" /> Mensagens</span>
                  {unreadCount > 0 && (
                    <span className="bg-destructive text-white text-xs px-1.5 py-0.5 rounded-full font-bold">
                      {unreadCount > 9 ? "9+" : unreadCount}
                    </span>
                  )}
                </Link>
                {isAdmin && (
                  <Link to="/admin" className="flex items-center gap-3 px-4 py-3 hover:bg-white/5 text-sm transition-colors" onClick={() => setOpen(false)}>
                    <ShieldCheck className="h-4 w-4 text-white/60" /> Admin {pendingCount > 0 && <span className="ml-1 bg-destructive text-white text-xs font-bold px-1.5 py-0.5 rounded-full leading-none">{pendingCount}</span>}
                  </Link>
                )}
                <button className="flex items-center gap-3 px-4 py-3 text-left w-full text-sm text-red-300 hover:bg-red-500/10 transition-colors" onClick={() => { logout(); setOpen(false); }}><LogOut className="h-4 w-4" /> Sair</button>
              </div>
            </>
          ) : (
            <div className="border-t border-white/20 mt-3 pt-3 space-y-2">
              <p className="text-xs text-primary-foreground/50 uppercase tracking-wider mb-2">Minha conta</p>
              <Link to="/login" onClick={() => setOpen(false)}>
                <button className="w-full py-2.5 rounded-lg border border-white/30 text-primary-foreground text-sm font-medium hover:bg-white/10 transition-colors">
                  Entrar
                </button>
              </Link>
              <Link to="/cadastro" onClick={() => setOpen(false)}>
                <button className="w-full py-2.5 rounded-lg bg-accent text-accent-foreground text-sm font-semibold hover:bg-accent/90 transition-colors mt-2">
                  Cadastrar gratuitamente
                </button>
              </Link>
            </div>
          )}
        </div>
      )}
    </nav>
  );
}
